import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import { BookingRequest, QuoteAcceptRequest, QuotesRequest, RequestClass } from 'acscp/api/requests';
import { Dispatch } from 'redux';
import { AppState, Booking } from 'acscp/store';
import { AddNotification, FetchRequest, FetchResetStatus } from 'acscp/actions';
import { RouteComponentProps } from 'react-router-dom';
import { withPageLayout } from 'acscp/components/layout/pageLayout';
import { Quote } from 'acscp/store/quotesState';
import React from 'react';
import { ThankYou } from 'acscp/components/quotes/thankYou';
import { QuoteAccept } from 'acscp/components/quotes/quoteAccept';
import { getBookingQuotes } from 'acscp/store/helpers/getBookingQuotes';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { defineMessages } from 'react-intl';
import { userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { NotificationType } from 'acscp/store/notificationState';
import { LoginRequiredModal } from '../auth/loginRequiredModal';

const messages = defineMessages({
    anotherAccepted: {
        id: 'components.pages.quoteAcceptPage.anotherAccepted',
        defaultMessage: 'Another quote has been already accepted.',
    },
});

const anotherQuoteAcceptedCause = 'AnotherQuoteAcceptedCause';

interface QuoteAcceptPageStateProps {
    quote?: Quote;
    booking: Booking;
    fetchHasCompleted: boolean;
    acceptRequestIsLoading: boolean;
    bookingHasAcceptedQuote: boolean;
    isLoggedIn: boolean;
    jetCardNo: string | null | undefined;
    jetCardType: string | null;
}

interface QuoteAcceptPageDispatchProps {
    acceptQuote: (notes: string) => void;
    fetchQuotes: () => void;
    fetchBookingDetails: () => void;
    clearQuoteRequestErrors: () => void;
    addAnotherQuoteAcceptedNotification: () => void;
}

export interface QuoteAcceptPageParams {
    bookingId: string;
    quoteId: string;
}
type QuoteAcceptPageOwnProps = RouteComponentProps<QuoteAcceptPageParams>;
type QuoteAcceptPageProps = QuoteAcceptPageOwnProps & QuoteAcceptPageStateProps & QuoteAcceptPageDispatchProps;

export class QuoteAcceptPagePresentational extends React.Component<QuoteAcceptPageProps, {}> {

    componentDidMount(): void {
        this.enforceValidProps(this.props);

        if (!this.props.quote) {
            this.props.fetchQuotes();
        }

        if (this.props.jetCardNo === undefined) {
            this.props.fetchBookingDetails();
        }
    }

    componentWillReceiveProps(nextProps: QuoteAcceptPageProps): void {
        this.enforceValidProps(nextProps);
    }

    componentWillUnmount(): void {
        this.props.clearQuoteRequestErrors();
    }

    private enforceValidProps(props: QuoteAcceptPageProps): void {
        const {
            addAnotherQuoteAcceptedNotification,
            bookingHasAcceptedQuote,
            fetchHasCompleted,
            history,
            quote
        } = props;

        if (quote) {
            if (bookingHasAcceptedQuote && quote.status !== 'Accepted') {
                // Another quote has been accepted.
                // This can be a valid state if this page was accessed using a direct link.
                addAnotherQuoteAcceptedNotification();

                const bookingId = props.match.params.bookingId;
                history.push(`/trip/${bookingId}`);
            }
        } else {
            if (fetchHasCompleted) {
                // The quote is missing - we can't show anything useful here.
                // Most likely this was caused by mismatching booking and quote ids in the URL
                history.push('/not-found');
            }
        }
    }

    render(): JSX.Element {
        const {quote, jetCardNo, jetCardType, match, isLoggedIn, ...rest} = this.props;
        const bookingId = match.params.bookingId;

        if (!quote || jetCardNo === undefined) {
            return <LoadingIndicator />;
        }

        if (!isLoggedIn) {
            return <LoginRequiredModal closeUrl={'/trip/' + this.props.booking.id} />;
        } else if (quote.status === 'Accepted') {
            return <ThankYou isBookingOwner={this.props.booking.ownedByCurrentUser} />;
        } else {
            return (
                <QuoteAccept
                    quote={quote}
                    bookingId={bookingId}
                    jetCardNo={jetCardNo}
                    jetCardType={jetCardType}
                    {...rest}
                />
            );
        }
    }
}

function mapStateToProps(
    state: AppState,
    ownProps: QuoteAcceptPageOwnProps,
    getRequestError: (request: RequestClass<any>) => RequestStateProps
): QuoteAcceptPageStateProps {
    const { bookingId, quoteId } = ownProps.match.params;

    let quote: Quote | undefined = quoteId ? state.quotes.quotesList[quoteId] : undefined;
    if (quote && quote.bookingId !== bookingId) {
        // We were passed an incorrect bookingId or quoteId. Fail by making the quote argument invalid
        quote = undefined;
    }

    const booking = state.bookingsDetails[bookingId];

    let bookingHasAcceptedQuote = false;
    if (quote) {
        const quotes = getBookingQuotes(state.quotes, bookingId);

        bookingHasAcceptedQuote = quotes.some(q => q.status === 'Accepted');
    }

    const quoteFetchState = getRequestError(QuotesRequest);
    const fetchHasCompleted = quoteFetchState.success || !!quoteFetchState.requestError;

    const acceptRequestIsLoading = getRequestError(QuoteAcceptRequest).isLoading;

    const [jetCardNo, jetCardType] = booking ? [booking.jetCardNo, booking.jetCardType] : [undefined, null];

    return {
        quote,
        booking,
        fetchHasCompleted,
        acceptRequestIsLoading,
        bookingHasAcceptedQuote,
        isLoggedIn: userIsLoggedIn(state.auth),
        jetCardNo,
        jetCardType
    };
}

function mapDispatchToProps(
    dispatch: Dispatch, ownProps: QuoteAcceptPageOwnProps
): QuoteAcceptPageDispatchProps {
    return {
        acceptQuote: (notes: string) =>
            dispatch(new FetchRequest(new QuoteAcceptRequest(ownProps.match.params.quoteId, notes))),
        fetchQuotes: () => dispatch(new FetchRequest(new QuotesRequest(ownProps.match.params.bookingId))),
        fetchBookingDetails: () =>
            dispatch(new FetchRequest(new BookingRequest(ownProps.match.params.bookingId))),
        clearQuoteRequestErrors: () => dispatch(new FetchResetStatus(QuotesRequest)),
        addAnotherQuoteAcceptedNotification: () =>
            dispatch(new AddNotification(anotherQuoteAcceptedCause, messages.anotherAccepted, NotificationType.Error)),
    };
}

export const QuoteAcceptPage =
    withPageLayout(connectWithRequests(mapStateToProps, mapDispatchToProps)(QuoteAcceptPagePresentational));
